"use client";

import { ReactNode, useCallback, useEffect, useState } from "react";
import {
  selectIsAuthSuccessOrPerpetual,
  selectLeadStatus,
  useAppSelector,
} from "@repo/web-store";
import { trackTickerStripDisplayBannerIconClick } from "./unifiedCt";
import { PORTFOLIO_DEMAT_CTA_ID } from "../../constants/dematBanner";

interface OpenDematFloatBannerVisibilityClientProps {
  children: ReactNode;
  floatingBannerMainText?: string;
  brokerageMainText?: string;
  loadOnPageLoad?: boolean;
  isPdp?: boolean;
}

const OpenDematFloatBannerVisibilityClient = ({
  children,
  floatingBannerMainText,
  brokerageMainText,
  loadOnPageLoad = false,
  isPdp = false,
}: OpenDematFloatBannerVisibilityClientProps) => {
  const [showBanner, setShowBanner] = useState(loadOnPageLoad);
  const [manuallyClosed, setManuallyClosed] = useState(false);
  const leadStatus = useAppSelector(selectLeadStatus);
  const isAuthSuccessOrPerpetual = useAppSelector(selectIsAuthSuccessOrPerpetual);
  const [isPortfolioCtaInView, setIsPortfolioCtaInView] = useState(false);
  // ETB keeps the banner (Trade Now); others would duplicate the Portfolio CTA
  const shouldWatchPortfolioCta = isAuthSuccessOrPerpetual && leadStatus !== "ETB";

  useEffect(() => {
    if (!shouldWatchPortfolioCta) {
      setIsPortfolioCtaInView(false);
      return;
    }

    let observedCta: HTMLElement | null = null;
    const intersectionObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry) setIsPortfolioCtaInView(entry.isIntersecting);
      },
      { threshold: 0.1, rootMargin: "-70px 0px 0px 0px" }, // CTA behind the sticky header counts as out of view
    );

    // The CTA mounts/unmounts with lead status, so re-bind whenever the node changes
    const syncObservedCta = () => {
      const cta = document.getElementById(PORTFOLIO_DEMAT_CTA_ID);
      if (cta === observedCta) return;
      intersectionObserver.disconnect();
      observedCta = cta;
      if (cta) {
        intersectionObserver.observe(cta);
      } else {
        setIsPortfolioCtaInView(false);
      }
    };

    const mutationObserver = new MutationObserver(syncObservedCta);
    mutationObserver.observe(document.body, { childList: true, subtree: true });
    syncObservedCta();

    return () => {
      intersectionObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [shouldWatchPortfolioCta]);

  const trackTickerStripClose = useCallback(() => {
    void trackTickerStripDisplayBannerIconClick(
      floatingBannerMainText,
      brokerageMainText ?? "BROKERAGE",
    );
  }, [brokerageMainText, floatingBannerMainText]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (loadOnPageLoad) return;

    let ticking = false;

    const updateBannerVisibility = () => {
      if (manuallyClosed) return;

      const shouldShow = window.scrollY > 100;
      setShowBanner((prev) => {
        if (prev !== shouldShow) return shouldShow;
        return prev;
      });

      ticking = false;
    };

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(updateBannerVisibility);
        ticking = true;
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    updateBannerVisibility();

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [manuallyClosed, loadOnPageLoad]);

  const handleClose = () => {
    setShowBanner(false);
    setManuallyClosed(true);
    trackTickerStripClose();
  };

  if (!showBanner) return null;

  return (
    <div className={isPortfolioCtaInView ? "hidden" : "contents"}>
      {children}
      <button
        className={`bg-lgray-200 fixed bottom-0 right-0 z-[100000] hidden min-h-[73px] w-[73px] cursor-pointer items-center justify-center md:flex${
          leadStatus === "ETB" && isPdp === true ? " md:hidden" : ""
        }`}
        onClick={handleClose}
        aria-label="Close banner"
      >
        <i className="icomoon text-[20px] before:content-['\e914']"></i>
      </button>
    </div>
  );
};

export default OpenDematFloatBannerVisibilityClient;
